import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Pause, RotateCcw, Zap, Timer as TimerIcon, ShieldCheck, ListChecks, ArrowLeft, Bell, ImageDown, MessageCircle, Instagram, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";
import WorkoutsTab from "@/pages/WorkoutsTab";

const authHeader = () => ({ headers: { "X-Access-Token": localStorage.getItem("guardia_bp_token") || "" } });

// ---- combo pools ----
const COMBOS = {
  beginner: [
    ["Jab", "Cross"],
    ["Jab", "Jab", "Cross"],
    ["Jab", "Cross", "Lead Hook"],
    ["Cross", "Lead Hook"],
    ["Jab", "Lead Hook"],
    ["Jab", "Cross", "Slip"],
  ],
  intermediate: [
    ["Jab", "Cross", "Lead Hook", "Cross"],
    ["Jab", "Cross", "Lead Uppercut", "Rear Hook"],
    ["Double Jab", "Cross", "Low Kick"],
    ["Jab", "Cross", "Slip", "Cross"],
    ["Lead Hook", "Cross", "Lead Hook"],
    ["Jab", "Rear Low Kick", "Cross"],
  ],
  advanced: [
    ["Jab", "Cross", "Lead Hook", "Rear Uppercut", "Lead Hook"],
    ["Double Jab", "Cross", "Lead Body Hook", "Rear Uppercut", "Low Kick"],
    ["Jab", "Cross", "Slip", "Rear Uppercut", "Lead Hook", "Rear Low Kick"],
    ["Feint Jab", "Cross", "Lead Hook", "Switch Kick"],
    ["Jab", "Cross", "Lead Liver Hook", "Rear Hook", "Teep"],
    ["Overhand", "Lead Hook", "Rear Low Kick", "Cross"],
  ],
};

function beep(freq = 660, dur = 0.35, type = "square") {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    o.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    o.start();
    o.stop(ctx.currentTime + dur);
  } catch (e) {}
}
const bell = () => {
  beep(880, 0.5, "triangle");
  setTimeout(() => beep(660, 0.5, "triangle"), 180);
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function BlueprintConsole() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("workouts");

  useEffect(() => {
    const token = localStorage.getItem("guardia_bp_token");
    if (!token) return setAuthed(false);
    api.get("/blueprint/verify", { params: { token } }).then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <div className="min-h-screen grid place-items-center text-zinc-500">Loading…</div>;
  if (authed === false)
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center bg-[#0c0d10]">
        <div>
          <p className="text-zinc-300 max-w-sm mx-auto">{t("access_needed")}</p>
          <Link to="/blueprint" className="mt-4 inline-block text-[#E63946] font-bold uppercase">
            {t("bp_title")} →
          </Link>
        </div>
      </div>
    );

  const tabs = [
    { id: "workouts", label: t("tab_workouts"), icon: Dumbbell },
    { id: "timer", label: t("tab_timer"), icon: TimerIcon },
    { id: "combos", label: t("tab_combos"), icon: Zap },
    { id: "checklist", label: t("tab_checklist"), icon: ShieldCheck },
    { id: "log", label: t("tab_log"), icon: ListChecks },
  ];

  return (
    <div className="min-h-screen bg-[#0c0d10]">
      <header className="border-b border-[#272b35] sticky top-0 z-40 bg-[#0c0d10]/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span className="font-heading text-xl font-black uppercase text-white">
            {t("console_title")}<span className="text-[#E63946]">.</span>
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="grid grid-cols-5 gap-2">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`console-tab-${tb.id}`}
              className={`flex flex-col items-center gap-1 py-3 rounded-lg border text-xs font-semibold transition-colors ${
                tab === tb.id ? "bg-[#E63946] border-[#E63946] text-white" : "border-[#272b35] text-zinc-400 hover:text-white"
              }`}
            >
              <tb.icon size={18} />
              <span className="hidden sm:block">{tb.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "workouts" && <WorkoutsTab initialProgram={new URLSearchParams(window.location.search).get("program")} />}
          {tab === "timer" && <TimerTab />}
          {tab === "combos" && <ComboTab />}
          {tab === "checklist" && <ChecklistTab />}
          {tab === "log" && <LogTab />}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------- Timer
function TimerTab() {
  const { t } = useLang();
  const [rounds, setRounds] = useState(3);
  const [roundLen, setRoundLen] = useState(180);
  const [restLen, setRestLen] = useState(60);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("work"); // work | rest | done
  const [current, setCurrent] = useState(1);
  const [remaining, setRemaining] = useState(180);
  const [completed, setCompleted] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r > 1) return r - 1;
        // phase transition
        bell();
        if (phase === "work") {
          setCompleted((c) => c + 1);
          if (current >= rounds) {
            setPhase("done");
            setRunning(false);
            return 0;
          }
          setPhase("rest");
          return restLen;
        } else {
          setPhase("work");
          setCurrent((c) => c + 1);
          return roundLen;
        }
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running, phase, current, rounds, roundLen, restLen]);

  const start = () => {
    if (phase === "done") reset();
    bell();
    setRunning(true);
  };
  const reset = () => {
    setRunning(false);
    setPhase("work");
    setCurrent(1);
    setRemaining(roundLen);
    setCompleted(0);
  };
  useEffect(() => {
    if (!running) setRemaining(roundLen);
  }, [roundLen]); // eslint-disable-line

  const saveSession = async () => {
    try {
      await api.post(
        "/blueprint/sessions",
        { rounds_completed: completed, total_minutes: Math.round((completed * roundLen) / 60), rpe: 6, strikes: 0, notes: "Round timer session", combos: [] },
        authHeader()
      );
      toast.success(t("session_saved"));
    } catch (e) {
      toast.error("Error");
    }
  };

  const isRest = phase === "rest";
  const done = phase === "done";

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div
        className={`rounded-2xl border p-8 flex flex-col items-center justify-center min-h-[320px] transition-colors ${
          done ? "border-[#10B981] bg-[#10B981]/5" : isRest ? "border-[#2563EB] bg-[#2563EB]/5" : "border-[#E63946] bg-[#E63946]/5"
        }`}
      >
        <span
          className={`text-xs font-mono uppercase tracking-[0.3em] ${done ? "text-[#10B981]" : isRest ? "text-[#2563EB]" : "text-[#E63946]"}`}
        >
          {done ? t("complete") : isRest ? t("rest") : `${t("round")} ${current}/${rounds}`}
        </span>
        <div data-testid="timer-display" className="font-mono-data font-bold text-white text-7xl sm:text-8xl my-4 tabular-nums">
          {fmt(remaining)}
        </div>
        <div className="text-xs text-zinc-500 font-mono">
          {t("round")} {completed} {t("complete")}
        </div>
        <div className="mt-6 flex gap-3">
          {!running ? (
            <button
              onClick={start}
              data-testid="interactive-timer-start-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md"
            >
              <Play size={18} /> {t("start")}
            </button>
          ) : (
            <button
              onClick={() => setRunning(false)}
              data-testid="timer-pause-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#F59E0B] text-black font-bold uppercase rounded-md"
            >
              <Pause size={18} /> {t("pause")}
            </button>
          )}
          <button
            onClick={reset}
            data-testid="timer-reset-btn"
            className="inline-flex items-center gap-2 px-4 py-3 border border-[#3a3f4b] hover:border-white text-white font-bold uppercase rounded-md"
          >
            <RotateCcw size={18} />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        <Stepper label={`${t("rounds")}`} value={rounds} set={setRounds} min={1} max={12} step={1} suffix="" disabled={running} testid="rounds" />
        <Stepper label={t("round_len")} value={roundLen} set={setRoundLen} min={60} max={300} step={30} suffix="s" disabled={running} testid="roundlen" />
        <Stepper label={t("rest_len")} value={restLen} set={setRestLen} min={15} max={120} step={15} suffix="s" disabled={running} testid="restlen" />
        <button
          onClick={saveSession}
          disabled={completed === 0}
          data-testid="save-session-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#3a3f4b] hover:border-white text-white font-bold uppercase rounded-md disabled:opacity-40"
        >
          <Bell size={16} /> {t("save_session")}
        </button>
      </div>
    </div>
  );
}

function Stepper({ label, value, set, min, max, step, suffix, disabled, testid }) {
  return (
    <div className="bg-[#14161b] border border-[#272b35] rounded-lg p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{label}</span>
        <span className="font-mono-data text-white font-bold text-lg">
          {suffix === "s" ? fmt(value) : value}
          {suffix === "s" ? "" : suffix}
        </span>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          disabled={disabled}
          data-testid={`stepper-${testid}-dec`}
          onClick={() => set(Math.max(min, value - step))}
          className="flex-1 py-2 bg-[#0c0d10] border border-[#272b35] rounded-md text-white font-bold disabled:opacity-40"
        >
          −
        </button>
        <button
          disabled={disabled}
          data-testid={`stepper-${testid}-inc`}
          onClick={() => set(Math.min(max, value + step))}
          className="flex-1 py-2 bg-[#0c0d10] border border-[#272b35] rounded-md text-white font-bold disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------- Combos
function ComboTab() {
  const { lang, t } = useLang();
  const [level, setLevel] = useState("beginner");
  const [combo, setCombo] = useState(null);
  const [history, setHistory] = useState([]);

  const generate = () => {
    const pool = COMBOS[level];
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setCombo(pick);
    setHistory((h) => [pick, ...h].slice(0, 6));
    beep(520, 0.15, "sine");
  };

  const levels = [
    { id: "beginner", label: t("beginner") },
    { id: "intermediate", label: t("intermediate") },
    { id: "advanced", label: t("advanced") },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-[#14161b] border border-[#272b35] rounded-2xl p-6">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{t("level")}</span>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {levels.map((l) => (
            <button
              key={l.id}
              data-testid={`combo-level-${l.id}`}
              onClick={() => setLevel(l.id)}
              className={`py-2 text-xs font-semibold rounded-md border transition-colors ${
                level === l.id ? "bg-[#E63946] border-[#E63946] text-white" : "border-[#272b35] text-zinc-400"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="mt-6 min-h-[160px] grid place-items-center bg-[#0c0d10] border border-[#272b35] rounded-xl p-6">
          {combo ? (
            <div className="flex flex-wrap gap-2 justify-center" data-testid="combo-output">
              {combo.map((mv, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  <span className="px-3 py-2 bg-[#E63946]/15 border border-[#E63946]/40 text-white font-heading uppercase font-bold rounded-md">
                    {mv}
                  </span>
                  {i < combo.length - 1 && <span className="text-zinc-600">→</span>}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-zinc-600 text-sm">{lang === "it" ? "Premi genera per la tua combo" : "Press generate for your combo"}</span>
          )}
        </div>

        <button
          onClick={generate}
          data-testid="combo-generate-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md"
        >
          <Zap size={18} /> {t("generate")}
        </button>
      </div>

      <div className="bg-[#14161b] border border-[#272b35] rounded-2xl p-6">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">History</span>
        <div className="mt-4 space-y-2">
          {history.length === 0 && <p className="text-zinc-600 text-sm">—</p>}
          {history.map((c, i) => (
            <div key={i} className="text-sm text-zinc-300 font-mono border-b border-[#272b35] pb-2">
              {c.join(" → ")}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------- Checklist
function ChecklistTab() {
  const { lang, t } = useLang();
  const items = lang === "it"
    ? [
        { key: "stance_width", label: "Larghezza guardia" },
        { key: "chin_tuck", label: "Mento protetto (chin tuck)" },
        { key: "hip_torque", label: "Torsione dell'anca" },
        { key: "pivot", label: "Pivot del piede" },
        { key: "guard_return", label: "Ritorno in guardia" },
      ]
    : [
        { key: "stance_width", label: "Stance width" },
        { key: "chin_tuck", label: "Chin tuck" },
        { key: "hip_torque", label: "Hip torque" },
        { key: "pivot", label: "Foot pivot" },
        { key: "guard_return", label: "Guard return" },
      ];
  const [vals, setVals] = useState(Object.fromEntries(items.map((i) => [i.key, 0])));
  const [score, setScore] = useState(null);

  const submit = async () => {
    try {
      const { data } = await api.post("/blueprint/checklist", vals, authHeader());
      setScore(data.score);
      beep(700, 0.2, "sine");
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <p className="text-sm text-zinc-400 mb-5">{t("checklist_intro")}</p>
      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.key} className="bg-[#14161b] border border-[#272b35] rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-zinc-200">{it.label}</span>
              <span className="font-mono-data text-[#E63946] font-bold">{vals[it.key]}/5</span>
            </div>
            <div className="flex gap-2">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`checklist-${it.key}-${n}`}
                  onClick={() => setVals((v) => ({ ...v, [it.key]: n }))}
                  className={`flex-1 py-2 rounded-md text-sm font-bold transition-colors ${
                    vals[it.key] >= n && n > 0 ? "bg-[#E63946] text-white" : "bg-[#0c0d10] border border-[#272b35] text-zinc-500"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={submit}
        data-testid="checklist-submit-btn"
        className="mt-6 w-full px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md"
      >
        {t("submit_checklist")}
      </button>
      {score !== null && (
        <div data-testid="checklist-score" className="mt-6 text-center bg-[#14161b] border border-[#10B981]/40 rounded-xl p-6">
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{t("your_score")}</span>
          <div className="font-mono-data text-5xl font-bold text-[#10B981] mt-2">{score}%</div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------- Log
function LogTab() {
  const { t } = useLang();
  const [sessions, setSessions] = useState([]);
  const [exp, setExp] = useState(null);
  const cardRef = useRef(null);
  const storyRef = useRef(null);

  useEffect(() => {
    api.get("/blueprint/sessions", authHeader()).then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!exp) return;
    let cancelled = false;
    (async () => {
      const html2canvas = (await import("html2canvas")).default;
      await new Promise((r) => setTimeout(r, 80));
      const node = exp.mode === "ig" ? storyRef.current : cardRef.current;
      if (cancelled || !node) return;
      const canvas = await html2canvas(node, { backgroundColor: "#0c0d10", scale: 2 });
      const dataUrl = canvas.toDataURL("image/png");

      const doDownload = () => {
        const link = document.createElement("a");
        link.download = `guardia-session-${exp.session.id}${exp.mode === "ig" ? "-story" : ""}.png`;
        link.href = dataUrl;
        link.click();
      };

      if (exp.mode === "download") {
        doDownload();
        toast.success("Card exported");
      } else {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], `guardia-session-${exp.session.id}.png`, { type: "image/png" });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text: t("share_text") });
          } else if (exp.mode === "wa") {
            window.open(`https://wa.me/?text=${encodeURIComponent(t("share_text"))}`, "_blank", "noopener");
            doDownload();
            toast.info("Card scaricata — allegala su WhatsApp");
          } else {
            doDownload();
            toast.info("Story 9:16 scaricata — caricala nella tua Storia Instagram");
          }
        } catch (err) {
          doDownload();
        }
      }
      if (!cancelled) setExp(null);
    })();
    return () => { cancelled = true; };
  }, [exp, t]);

  return (
    <div className="max-w-2xl mx-auto">
      {sessions.length === 0 ? (
        <p className="text-center text-zinc-600 py-12" data-testid="no-logs">{t("no_logs")}</p>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <div key={s.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#14161b] border border-[#272b35] rounded-lg p-4">
              <div>
                <div className="font-heading text-lg font-bold uppercase text-white">
                  {s.rounds_completed} {t("round")} · {s.total_minutes} min
                </div>
                {s.notes && <div className="text-xs text-[#E63946] font-semibold mt-0.5">{s.notes}</div>}
                <div className="text-xs text-zinc-500 font-mono">{new Date(s.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-data text-[#E63946] font-bold mr-1">RPE {s.rpe}</span>
                <button
                  onClick={() => setExp({ session: s, mode: "download" })}
                  data-testid={`export-card-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 border border-[#3a3f4b] hover:border-white text-white text-xs font-bold uppercase rounded-md"
                >
                  <ImageDown size={14} />
                </button>
                <button
                  onClick={() => setExp({ session: s, mode: "wa" })}
                  data-testid={`share-wa-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-xs font-bold uppercase rounded-md hover:bg-[#25D366]/25"
                >
                  <MessageCircle size={14} /> {t("share_wa")}
                </button>
                <button
                  onClick={() => setExp({ session: s, mode: "ig" })}
                  data-testid={`share-ig-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 bg-[#E1306C]/15 border border-[#E1306C]/40 text-[#E1306C] text-xs font-bold uppercase rounded-md hover:bg-[#E1306C]/25"
                >
                  <Instagram size={14} /> {t("share_ig")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* off-screen export card */}
      {exp && (
        <div style={{ position: "fixed", left: -9999, top: 0 }}>
          <div ref={cardRef} style={{ width: 540, padding: 40, background: "#0c0d10", fontFamily: "Arial, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #272b35", paddingBottom: 16 }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", textTransform: "uppercase", letterSpacing: -1 }}>
                GUARDIA<span style={{ color: "#E63946" }}>.</span>
              </div>
              <div style={{ fontSize: 11, letterSpacing: 3, color: "#E63946", textTransform: "uppercase" }}>Field Log Card</div>
            </div>
            <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Stat label="Rounds" value={exp.session.rounds_completed} />
              <Stat label="Minutes" value={exp.session.total_minutes} />
              <Stat label="RPE" value={`${exp.session.rpe}/10`} />
              <Stat label="Strikes" value={exp.session.strikes || "—"} />
            </div>
            <div style={{ marginTop: 24, padding: 16, background: "#14161b", border: "1px solid #272b35", borderRadius: 10 }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: "#9CA3AF", textTransform: "uppercase" }}>Session</div>
              <div style={{ fontSize: 14, color: "#e4e4e7", marginTop: 6 }}>{exp.session.notes || "Striker Blueprint session"}</div>
            </div>
            <div style={{ marginTop: 20, fontSize: 11, color: "#6B7280" }}>
              {new Date(exp.session.created_at).toLocaleString()} · The 8-Week Striker Blueprint
            </div>
          </div>

          {/* Instagram Story 9:16 card */}
          <div ref={storyRef} style={{ width: 432, height: 768, background: "linear-gradient(160deg,#14161b 0%,#0c0d10 60%)", padding: 40, fontFamily: "Arial, sans-serif", position: "relative", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
            {/* logo sticker */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, alignSelf: "flex-start", background: "#E63946", padding: "8px 14px", borderRadius: 999, boxShadow: "0 8px 24px rgba(230,57,70,0.5)" }}>
              <span style={{ fontSize: 18, fontWeight: 900, color: "#fff", textTransform: "uppercase", letterSpacing: -0.5 }}>GUARDIA</span>
              <span style={{ fontSize: 10, color: "#fff", textTransform: "uppercase", letterSpacing: 2, opacity: 0.85 }}>Iron Glove</span>
            </div>

            <div style={{ marginTop: 48 }}>
              <div style={{ fontSize: 12, letterSpacing: 4, color: "#E63946", textTransform: "uppercase" }}>Training Session</div>
              <div style={{ fontSize: 30, fontWeight: 900, color: "#fff", textTransform: "uppercase", lineHeight: 1.05, marginTop: 8 }}>
                Striker<br />Blueprint
              </div>
            </div>

            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
              <StoryStat label="Rounds" value={exp.session.rounds_completed} />
              <StoryStat label="Minutes" value={exp.session.total_minutes} />
              <StoryStat label="RPE" value={`${exp.session.rpe}/10`} />
            </div>

            <div style={{ marginTop: 28, paddingTop: 16, borderTop: "1px solid #272b35", fontSize: 11, color: "#9CA3AF", letterSpacing: 1 }}>
              {new Date(exp.session.created_at).toLocaleDateString()} · guardia.fit
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ padding: 16, background: "#14161b", border: "1px solid #272b35", borderRadius: 10 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: "#9CA3AF", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 34, fontWeight: 700, color: "#E63946", fontFamily: "monospace", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function StoryStat({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderBottom: "1px solid #1f232c", paddingBottom: 10 }}>
      <span style={{ fontSize: 13, letterSpacing: 2, color: "#9CA3AF", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 44, fontWeight: 800, color: "#fff", fontFamily: "monospace", lineHeight: 1 }}>{value}</span>
    </div>
  );
}
